/* eslint-disable react/no-array-index-key */
import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { useTranslate } from 'hooks/useTranslate';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';
import { dateFormat } from 'constants/formats';
import {
  IncentiveOffer,
  BeneficiaryAgreedIncentiveOffer,
} from 'modules/incentivesMarket/types';

import CustomLoading from 'modules/_shared/components/CustomLoading';

import { IncentiveOfferOperationsSummary } from '../types/IncentiveOfferDetailContextType';

type IncentiveOfferAcceptanceProgressProps = {
  offer: IncentiveOffer;
  acceptanceHistory: BeneficiaryAgreedIncentiveOffer[];
  operationsSummary: IncentiveOfferOperationsSummary;
  isLoadingOperations?: boolean;
};

const IncentiveOfferAcceptanceProgress: FC<
  IncentiveOfferAcceptanceProgressProps
> = ({
  offer,
  acceptanceHistory,
  operationsSummary,
  isLoadingOperations = false,
}) => {
  const { t } = useTranslate();

  const totalShares = offer.unitsCount.total;
  const agreedShares = offer.unitsCount.agreed;
  const availableShares = totalShares - agreedShares;

  const progressPercent = useMemo(() => {
    if (totalShares === 0) return 0;
    return Math.round((agreedShares / totalShares) * 100);
  }, [agreedShares, totalShares]);

  return (
    <div className="card card-bordered mb-4">
      <div className="card-inner">
        <h6 className="card-title mb-3">
          <em className="icon ni ni-pie text-primary mr-2" />
          {t('AcceptanceProgress')}
        </h6>

        <div className="progress progress-lg mb-3" style={{ height: '24px' }}>
          <div
            className="progress-bar bg-info"
            role="progressbar"
            style={{ width: `${progressPercent}%` }}
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            {progressPercent}% {t('Accepted').toLowerCase()}
          </div>
        </div>

        <div className="row">
          {/* <div className="col-4 text-center border-right">
            <span className="d-block text-soft fs-12px">{t('Offered')}</span>
            <strong className="fs-16px">{formatNumber(totalShares)}</strong>
          </div> */}
          {/* <div className="col-4 text-center border-right">
            <span className="d-block text-soft fs-12px">{t('Accepted')}</span>
            <strong className="fs-16px text-info">
              {formatNumber(agreedShares)}
            </strong>
          </div> */}
          <div className="col-4 text-center border-right">
            <span className="d-block text-soft fs-12px">{t('Accepted')}</span>
            <strong className="fs-16px text-info">
              {formatNumber(operationsSummary.totalUnits)}
            </strong>
          </div>
          <div className="col-4 text-center border-right">
            <span className="d-block text-soft fs-12px">{t('Available')}</span>
            <strong className="fs-16px text-primary">
              {formatNumber(availableShares)}
            </strong>
          </div>
          <div className="col-4 text-center">
            <span className="d-block text-soft fs-12px">
              {t('TotalAmount')}
            </span>
            <strong className="fs-16px">
              {formatCurrencyDecimals(operationsSummary.totalAmount)}
            </strong>
          </div>
        </div>

        {/* <div className="row mt-3 pt-3 border-top">
          <div className="col-6 text-center border-right">
            <span className="d-block text-soft fs-12px">
              {t('IncentivesMarketTotalUnits')}
            </span>
            <strong className="fs-16px">
              {formatNumber(operationsSummary.totalUnits)}
            </strong>
          </div>
          <div className="col-6 text-center">
            <span className="d-block text-soft fs-12px">
              {t('TotalAmount')}
            </span>
            <strong className="fs-16px">
              {formatCurrencyDecimals(operationsSummary.totalAmount)}
            </strong>
          </div>
        </div> */}

        {acceptanceHistory.length > 0 && (
          <>
            <hr />
            <h6 className="overline-title text-soft mb-2">
              {t('AcceptanceHistory')}
            </h6>
            <ul className="list-unstyled mb-0">
              {acceptanceHistory.map((item, index) => (
                <li
                  key={index}
                  className="d-flex justify-content-between py-2 border-bottom"
                >
                  <span>
                    <em className="icon ni ni-user mr-1" />
                    {formatNumber(item.unitsCount.agreed)}{' '}
                    {t('IncentivesMarketUnits').toLowerCase()}
                  </span>
                  <span className="text-soft">
                    {format(new Date(item.date), dateFormat)}
                  </span>
                  {item.isApproved ? (
                    <span className="badge badge-dim badge-info">
                      {t('Formalized')}
                    </span>
                  ) : (
                    <span className="badge badge-dim badge-warning">
                      {t('Pending')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      {isLoadingOperations && <CustomLoading />}
    </div>
  );
};

export default IncentiveOfferAcceptanceProgress;
